// ./useGlobalDropHandler.ts
import { useCallback, useEffect, RefObject } from 'react';
import { Layer, LockableFile } from  '#types/types';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { useKernel } from '@/kernel/Kernel';
import { getNextRenderOrderForWorkspace } from '#lib/layer-render-order';
import { getProjectPanOffset, getProjectZoomLevel, setProjectSelectedLayersId } from '#lib/project-runtime-layer-ui-state';
import { buildStudioLayerMutationPayload } from '#lib/studio-runtime-dto';
import { resolveGenerationOutputScopeForView } from '#lib/generation-output-scope';
import { getStudioModelReferenceLimits } from '#lib/model-registry';
import { projectSurfaceOf } from '#lib/modelist/projectScene';

interface UseGlobalDropHandlerArgs {
  containerRef: RefObject<HTMLDivElement | null>;
  projectId: string;
}

const getDropZoneArea = (target: EventTarget | null): string => {
  const defaultArea = 'project'; // Define the default area name
  if (!(target instanceof Element)) return defaultArea;
  let current: Element | null = target;
  while (current) {
      // Check specifically for the 'drop-area' attribute
      if (current.hasAttribute('drop-area')) {
          // Return the attribute's value, defaulting if empty/null
          return current.getAttribute('drop-area')?.trim() || defaultArea;
      }
      current = current.parentElement;
  }
  return defaultArea; // Return default if no attribute found in ancestry
};

const getFirstDraggedUrl = (dataTransfer: DataTransfer): string | null => {
  const uriList = dataTransfer
    .getData('text/uri-list')
    .split(/\r?\n/)
    .map(line => line.trim())
    .find(line => line.length > 0 && !line.startsWith('#'));
  if (uriList) return uriList;

  const plainText = dataTransfer.getData('text/plain').trim();
  if (/^(blob:|data:image\/|https?:\/\/|\/)/i.test(plainText)) {
    return plainText;
  }

  return null;
};

const filenameFromUrl = (url: string, fallbackExtension: string): string => {
  try {
    const parsed = new URL(url, window.location.origin);
    const lastSegment = parsed.pathname.split('/').filter(Boolean).pop();
    if (lastSegment) return decodeURIComponent(lastSegment);
  } catch {
    // Keep fallback below for blob/data URLs or malformed-but-fetchable values.
  }
  return `dropped-reference-${Date.now()}.${fallbackExtension}`;
};

const createImageFileFromDraggedUrl = async (url: string): Promise<File | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    const mimeType = blob.type || (url.startsWith('data:image/') ? url.slice(5, url.indexOf(';')) : 'image/png');
    if (!mimeType.startsWith('image/')) return null;
    const extension = mimeType.split('/')[1]?.split('+')[0] || 'png';
    return new File([blob], filenameFromUrl(url, extension), { type: mimeType });
  } catch {
    return null;
  }
};

export function useGlobalDropHandler({
  containerRef,
  projectId,
}: UseGlobalDropHandlerArgs): void {
  const { kernel } = useKernel();
  // --- Handlers ---

  const handleFileDrop = useCallback(async (files: FileList, dropTargetArea: string, worldPos?: { x: number, y: number }) => { // Explicit type
    if (!files || files.length === 0 || !kernel.current) return;

    // A CHAT DROP WITHOUT A STUDIO PROJECT NEVER ENTERS STUDIO CODE. addFilesAsLayers is the
    // Studio import (layers, workspaces, undo) and rightly refuses to run without a project - but
    // Source/Dealer sessions have none, so files dropped on the portal chat died in that refusal,
    // silently (2026-08-23). Attach to the composer here, where the drop was routed, the same way
    // the dragged-URL path below always has. Studio sessions with a project keep the full import
    // routing untouched.
    if (dropTargetArea === 'imagesInChatInput' && !projectId.trim()) {
      const dropped = Array.from(files);
      const images = dropped.filter(
        (file) =>
          (file.type ?? '').toLowerCase().startsWith('image/') ||
          /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(file.name.toLowerCase()),
      );
      if (images.length > 0) {
        const lockableFiles: LockableFile[] = images.map((file) => ({ id: uuidv4(), file, locked: false }));
        kernel.current.setImagesInChatInput((prev) => [...prev, ...lockableFiles]);
        kernel.current.logUserAction('Image Dropped', { target: 'imagesInChatInput', count: lockableFiles.length });
      }
      if (images.length < dropped.length) {
        toast.info('Only images can be attached to this chat for now.');
      }
      return;
    }

    await kernel.current.addFilesAsLayers(files, dropTargetArea, worldPos);


  }, [kernel, projectId]);


  const handleDragOver = useCallback((event: DragEvent) => { event.preventDefault(); event.stopPropagation(); }, []);

  const handleDrop = useCallback(async (event: DragEvent) => {
    if (event.target instanceof Element && event.target.closest('[data-generic-image-input-drop-zone="true"]')) {
      return;
    }
    event.preventDefault();
    const determinedArea = getDropZoneArea(event.target); // Determine area using the new logic
    // This native listener fires before React's delegated onDrop, so the portaled timeline cannot stop it.
    if (determinedArea === 'timeline') return;
    const droppedFiles = event.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length > 0) {
      if (kernel.current?.canvasContainerRef.current && determinedArea === 'project') {
        const panOffset = getProjectPanOffset(kernel.current, projectId);
        const zoomLevel = getProjectZoomLevel(kernel.current, projectId);
        const rect = kernel.current.canvasContainerRef.current.getBoundingClientRect();
        const worldPos = {
            x: (event.clientX - rect.left - panOffset.x) / zoomLevel,
            y: (event.clientY - rect.top - panOffset.y) / zoomLevel,
        };
        handleFileDrop(droppedFiles, determinedArea, worldPos);
      } else {
        handleFileDrop(droppedFiles, determinedArea); // Pass determined area
      }
      return;
    }

    if (!event.dataTransfer || !kernel.current) return;
    const draggedUrl = getFirstDraggedUrl(event.dataTransfer);
    if (!draggedUrl) return;
    const file = await createImageFileFromDraggedUrl(draggedUrl);
    if (!file || !kernel.current) return;

    if (determinedArea === 'imagesInChatInput') {
      const lockableFile: LockableFile = { id: uuidv4(), file, locked: false };
      kernel.current.setImagesInChatInput((prev) => [...prev, lockableFile]);
      kernel.current.logUserAction('Image Dropped', { target: 'imagesInChatInput' });
      return;
    }

    if (determinedArea === 'extraFiles') {
      const normalizedProjectId = projectId.trim();
      if (!normalizedProjectId) return;
      const maxRefs = getStudioModelReferenceLimits(kernel.current.getDiffusionModel()).max;
      const lockableFile: LockableFile = { id: uuidv4(), file, locked: false };
      if (maxRefs > 1) {
        kernel.current.setExtraFiles(normalizedProjectId, (prev) => {
          const newFiles = [...prev, lockableFile];
          if (newFiles.length > maxRefs) {
            toast.info(`Reference image limit (${maxRefs}) reached. Oldest image was replaced.`);
            return newFiles.slice(newFiles.length - maxRefs);
          }
          return newFiles;
        });
      } else {
        kernel.current.setExtraFiles(normalizedProjectId, [lockableFile]);
      }
      kernel.current.logUserAction('Image Dropped', { target: 'extraFiles' });
      return;
    }

    if (determinedArea === 'videoLastImages') {
      const normalizedProjectId = projectId.trim();
      if (!normalizedProjectId) return;
      const lockableFile: LockableFile = { id: uuidv4(), file, locked: false };
      kernel.current.setVideoLastImages(normalizedProjectId, [lockableFile]);
      kernel.current.logUserAction('Image Dropped', { target: 'videoLastImages' });
    }
  }, [handleFileDrop, projectId, kernel]);


  const handlePaste = useCallback(async (event: ClipboardEvent) => {
    // A surface that takes its own files (the 3D composer) takes its own pastes too, as it does its drops.
    if (event.target instanceof Element && event.target.closest('[data-generic-image-input-drop-zone="true"]')) return;
    // A module canvas that took the paste first (Slides, in the capture phase) owns it.
    if (event.defaultPrevented) return;
    const normalizedProjectId = projectId.trim();
    if (kernel.current && projectSurfaceOf(kernel.current.getState(), normalizedProjectId) === 'scene') return;
    let processedItem = false;
    const currentUIWorkspaceId = normalizedProjectId
      ? (kernel.current?.getProjectUIWorkspaceId(normalizedProjectId) ?? '')
      : '';

    const pasteImageFile = async (file: File, determinedArea: string) => {
      if (!kernel.current) return;

      if (determinedArea === 'imagesInChatInput') {
        const lockableFile: LockableFile = { id: uuidv4(), file, locked: false };
        kernel.current.setImagesInChatInput((prev) => [...prev, lockableFile]);
        kernel.current.logUserAction('Image Pasted', { target: 'imagesInChatInput' });
        return;
      }

      if (determinedArea === 'extraFiles') {
        if (!normalizedProjectId) return;
        const maxRefs = getStudioModelReferenceLimits(kernel.current.getDiffusionModel()).max;
        const lockableFile: LockableFile = { id: uuidv4(), file, locked: false };
        if (maxRefs > 1) {
          kernel.current.setExtraFiles(normalizedProjectId, (prev) => {
            const newFiles = [...prev, lockableFile];
            if (newFiles.length > maxRefs) {
              toast.info(`Reference image limit (${maxRefs}) reached. Oldest image was replaced.`);
              return newFiles.slice(newFiles.length - maxRefs);
            }
            return newFiles;
          });
        } else {
          kernel.current.setExtraFiles(normalizedProjectId, [lockableFile]);
        }
        kernel.current.logUserAction('Image Pasted', { target: 'extraFiles' });
        return;
      }

      if (determinedArea === 'videoLastImages') {
        if (!normalizedProjectId) return;
        const lockableFile: LockableFile = { id: uuidv4(), file, locked: false };
        kernel.current.setVideoLastImages(normalizedProjectId, [lockableFile]);
        kernel.current.logUserAction('Image Pasted', { target: 'videoLastImages' });
        return;
      }

      if (!currentUIWorkspaceId) {
        // Outside a Studio project there is no canvas to paste onto: the module on screen handles it, or nothing does.
        if (normalizedProjectId) kernel.current.handleError('No active workspace selected for paste.');
        return;
      }
      await kernel.current.addLayerFromFile(currentUIWorkspaceId, file);
      kernel.current.logUserAction('Image Pasted', { target: 'layer' });
    };

    const directClipboardItems = Array.from(event.clipboardData?.items ?? []);
    const directImageItem = directClipboardItems.find((item) => item.type.startsWith('image/'));
    if (directImageItem) {
      const directFile = directImageItem.getAsFile();
      if (directFile) {
        event.preventDefault();
        event.stopPropagation();
        const extension = directFile.type.split('/')[1] || 'png';
        const normalizedFile = new File([directFile], `pasted-${Date.now()}.${extension}`, {
          type: directFile.type || 'image/png',
        });
        const determinedArea = getDropZoneArea(event.target);
        await pasteImageFile(normalizedFile, determinedArea);
        return;
      }
    }

    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        if (!item.types.includes('text/plain')) continue;
        const textBlob = await item.getType('text/plain');
        const text = await textBlob.text();
        if (!text || !text.trim().startsWith('[')) continue;

        try {
          const parsedLayers = JSON.parse(text);
          if (!Array.isArray(parsedLayers) || parsedLayers.length === 0) continue;
          if (!(typeof parsedLayers[0] === 'object' && parsedLayers[0] && 'rect' in parsedLayers[0] && 'name' in parsedLayers[0])) continue;

          event.preventDefault();
          event.stopPropagation();

          const layersToAdd: Layer[] = [];
          let timestampOffset = 0;
          const existingLayers = kernel.current?.getLayersInWorkspaceContext(currentUIWorkspaceId) ?? [];
          const baseRenderOrder = getNextRenderOrderForWorkspace(existingLayers, currentUIWorkspaceId);

          for (const layerData of parsedLayers) {
            const newId = `layer-${Date.now()}-${timestampOffset++}-${Math.random().toString(36).slice(2, 8)}`;
            const newLayer: Layer = {
              visible: true,
              locked: false,
              opacity: 1,
              isAltered: true,
              revealCutoffX: null,
              playRevealAnimation: false,
              ...layerData,
              id: newId,
              timestamp: Date.now(),
              layerWorkspaceId: currentUIWorkspaceId,
              name: `${layerData.name || 'Pasted Layer'} copy`,
              imageData: null,
              source: 'user-paste',
              renderOrder: baseRenderOrder + layersToAdd.length,
            };
            delete (newLayer as any).mustRender;
            delete (newLayer as any).x;
            delete (newLayer as any).y;
            layersToAdd.push(newLayer);
          }

          if (layersToAdd.length > 0 && kernel.current) {
            const k = kernel.current;
            const layerIds = layersToAdd.map((layer) => layer.id);
            layersToAdd.forEach((layer) => {
              k.setLayer(layer.id, layer);
              if (currentUIWorkspaceId) {
                k.attachLayerToWorkspace(currentUIWorkspaceId, layer.id);
              }
            });
            if (normalizedProjectId && currentUIWorkspaceId) {
              void k.queueStudioWrite(
                layersToAdd.map((layer) => ({
                  action: 'CREATE',
                  entity: 'StudioLayer',
                  id: layer.id,
                  payload: buildStudioLayerMutationPayload(layer, normalizedProjectId, currentUIWorkspaceId),
                })) as any
              ).catch((error) => {
                console.error('[useGlobalDropHandler] Failed to persist pasted layers.', error);
              });
            }
            if (normalizedProjectId) {
              setProjectSelectedLayersId(k, normalizedProjectId, layerIds);
            }
            if (currentUIWorkspaceId) {
              const outputScope = resolveGenerationOutputScopeForView(
                normalizedProjectId,
                currentUIWorkspaceId,
                k.getStudioViewMode(),
              );
              void k.ensureWorkspaceVersionAfterCompositionCommit(
                currentUIWorkspaceId,
                'Pasted Layers',
                outputScope ? { outputScope } : undefined,
              ).catch((error) => {
                console.error('[useGlobalDropHandler] Failed to ensure pasted-layer workspace version.', error);
              });
            }
            k.logUserAction('Layers Pasted', { count: layersToAdd.length });
          }

          processedItem = true;
          break;
        } catch {
          // Not a recognized layer JSON payload.
        }
      }

      if (!processedItem) {
        for (const item of clipboardItems) {
          const imageType = item.types.find((type) => type.startsWith('image/'));
          if (!imageType) continue;
          const blob = await item.getType(imageType);
          event.preventDefault();
          event.stopPropagation();
          const file = new File([blob], `pasted-${Date.now()}.${imageType.split('/')[1] || 'png'}`, { type: imageType });
          const determinedArea = getDropZoneArea(event.target);
          await pasteImageFile(file, determinedArea);
          processedItem = true;
          break;
        }
      }
    } catch (err: any) {
      if (err.name !== 'NotFoundError') {
        console.error("Clipboard read error:", err);
        if (err.name === 'NotAllowedError') kernel.current?.handleError("Clipboard permission denied.");
        else kernel.current?.handleError(`Clipboard error: ${err.message || 'Unknown'}`);
      }
    }
  }, [kernel, projectId]);

  // Effect to attach listeners (no changes needed here)
  useEffect(() => {
    const currentContainer = containerRef.current;

    if (currentContainer) {
      currentContainer.addEventListener('dragover', handleDragOver);
      currentContainer.addEventListener('drop', handleDrop);
    }
    document.addEventListener('paste', handlePaste);

    return () => {
      if (currentContainer) {
        currentContainer.removeEventListener('dragover', handleDragOver);
        currentContainer.removeEventListener('drop', handleDrop);
      }
      document.removeEventListener('paste', handlePaste);
    };
  }, [containerRef, handleDragOver, handleDrop, handlePaste]);
}


